// Constants
import { OperationStatus } from "@ms/planner-models/lib/local/client/operations/Enumerations";
import { DefaultContainerPermissions, DefaultDependencyPermissions, DefaultPlanPermissions, DefaultTaskPermissions } from "@ms/planner-models/lib/local/constants/PermissionsConstants";
import { ContextScenarioConstants } from "@ms/uno-constants/lib/local/ExternalSourceConstants";
import { TaskKind } from "@ms/planner-models/lib/local/client/planner/task/ITask";
import { ContainerType } from "@ms/planner-models/lib/local/service/planner/enumerations/IPlannerEnumerations";
import { createTempDependency } from "@ms/planner-models/lib/local/utilities/DependencyUtilities";
import { createTempTask } from "@ms/planner-models/lib/local/utilities/TaskUtilities";
// Utilities
import { LocalIdGenerator } from "@ms/uno-utilities/lib/local/LocalIdGenerator";
// Utilities
import { canAccessPlanViaContainers, canCurrentUserAccessContainer, getIsUserContainerOwner } from "./ContainerUtilities";
/**
 * Get the plan permissions, using data from multiple stores
 * @param plan The plan to get permissions for, if loaded
 * @param stores The stores to use to calculate the plan permissions
 * @param traceLogger Trace event logger
 */ export function getPlanPermissionsFromStores(plan, stores, traceLogger) {
    const { permissionsStore, appContextStore } = stores;
    if (plan === null) {
        return DefaultPlanPermissions;
    }
    const currentUserId = appContextStore.getAppContext().userId;
    const isOwner = currentUserId ? getIsUserContainerOwner(currentUserId, plan.container, stores) : false;
    return permissionsStore.getPlanPermissions(plan, canAccessPlanViaContainers(plan, stores, traceLogger), isOwner, getIsPlanCreationInProgress(plan.id, stores));
}
/**
 * Get the task permissions, using data from multiple stores
 * @param task The task to get permissions for
 * @param plan The plan to get permissions for, if loaded
 * @param stores The stores to use to calculate the task permissions
 * @param traceLogger Trace event logger
 */ export function getTaskPermissionsFromStores(task, plan, stores, traceLogger) {
    const { permissionsStore } = stores;
    return permissionsStore.getTaskPermissions(task, plan, plan ? canAccessPlanViaContainers(plan, stores, traceLogger) : true);
}
/**
 * Get the task permissions for a hypothetical NEW task scoped to `plan`.
 * Synthesizes a plan-scoped temp task and runs it through the same PermissionsStore path as
 * existing tasks ({@link getTaskPermissionsFromStores}), so a new task's capabilities stay in
 * lockstep with existing-row capabilities (license + sensitivity gates, etc.) instead of each
 * caller re-deriving the temp task and the stores bag.
 * @param plan The plan the new task would be created in, if loaded
 * @param stores The stores to use to calculate the task permissions
 * @param traceLogger Trace event logger
 */ export function getAddTaskPermissions(plan, stores, traceLogger) {
    if (plan === null) {
        return DefaultTaskPermissions;
    }
    const taskKind = plan.container.containerType === ContainerType.ToDo ? TaskKind.ToDo : TaskKind.Planner;
    const tempTask = createTempTask(LocalIdGenerator.generate(), plan.id, taskKind);
    return getTaskPermissionsFromStores(tempTask, plan, stores, traceLogger);
}
/**
 * Get the Checklist Item permissions, using data from multiple stores
 * @param task The task to get the checklist item permissions for
 * @param plan The plan to get permissions for, if loaded
 * @param stores The stores to use to calculate the checklist item permissions
 * @param traceLogger Trace event logger
 */ export function getChecklistItemPermissionsFromStores(task, plan, stores, traceLogger) {
    const { permissionsStore } = stores;
    return permissionsStore.getChecklistItemPermissions(task, plan, plan ? canAccessPlanViaContainers(plan, stores, traceLogger) : true);
}
/**
 * Get the Attachment permissions, using data from multiple stores
 * @param task The task to get the attachment permissions for
 * @param plan The plan to get permissions for, if loaded
 * @param stores The stores to use to calculate the attachment permissions
 * @param traceLogger Trace event logger
 */ export function getAttachmentPermissionsFromStores(task, plan, stores, traceLogger) {
    const { permissionsStore } = stores;
    return permissionsStore.getAttachmentPermissions(task, plan, plan ? canAccessPlanViaContainers(plan, stores, traceLogger) : true);
}
/**
 * Get the container permissions, using data from multiple stores
 * @param container The container to get permissions for, if loaded
 * @param stores The stores to use to calculate the container permissions
 * @param traceLogger Trace event logger
 */ export function getContainerPermissionsFromStores(container, stores, traceLogger) {
    const { appContextStore, permissionsStore, modernGroupsStore } = stores;
    if (container === null) {
        return DefaultContainerPermissions;
    }
    const currentUserId = appContextStore.getAppContext().userId;
    return permissionsStore.getContainerPermissions(container, canCurrentUserAccessContainer(container, stores, traceLogger), currentUserId ? getIsUserContainerOwner(currentUserId, container, stores) : false, modernGroupsStore.getModernGroup(container.containerId).data?.visibility);
}
/**
 * Get the custom field definition permissions, using data from multiple stores
 * @param customFieldDefinition The custom field definition to get permissions for
 * @param plan The plan the custom field definition belongs to, if loaded
 * @param stores The stores to use to calculate the permissions
 * @param traceLogger Trace event logger
 */ export function getCustomFieldDefinitionPermissionsFromStores(customFieldDefinition, plan, stores, traceLogger) {
    return stores.permissionsStore.getCustomFieldDefinitionPermissions(customFieldDefinition, plan, plan ? canAccessPlanViaContainers(plan, stores, traceLogger) : true);
}
/**
 * Get the per-goal entity permissions, using data from multiple stores
 * @param goal The goal to get permissions for
 * @param plan The plan the goal belongs to, if loaded
 * @param stores The stores to use to calculate the permissions
 * @param traceLogger Trace event logger
 */ export function getGoalsPermissionsFromStores(goal, plan, stores, traceLogger) {
    return stores.permissionsStore.getGoalsPermissions(goal, plan, plan ? canAccessPlanViaContainers(plan, stores, traceLogger) : true, plan ? getIsPlanCreationInProgress(plan.id, stores) : false);
}
/**
 * Get the dependency permissions, using data from multiple stores
 * @param dependency The dependency to get permissions for
 * @param plan The plan the dependency belongs to, if loaded
 * @param stores The stores to use to calculate the permissions
 * @param traceLogger Trace event logger
 */ export function getDependencyPermissionsFromStores(dependency, plan, stores, traceLogger) {
    return stores.permissionsStore.getDependencyPermissions(dependency, plan, plan ? canAccessPlanViaContainers(plan, stores, traceLogger) : true);
}
/**
 * Get the dependency permissions for a hypothetical NEW dependency in `plan`.
 * @param plan The plan the new dependency would belong to, if loaded
 * @param stores The stores to use to calculate the permissions
 * @param traceLogger Trace event logger
 */ export function getAddDependencyPermissions(plan, stores, traceLogger) {
    if (plan === null) {
        // Plan not loaded yet — no entity to resolve against. Mirrors getAddTaskPermissions and
        // avoids the throwaway temp dependency + store call (the store also defaults null plans).
        return DefaultDependencyPermissions;
    }
    const tempDependency = createTempDependency("", "");
    return getDependencyPermissionsFromStores(tempDependency, plan, stores, traceLogger);
}
export function getIsPlanCreationInProgress(planId, stores) {
    const { planStore } = stores;
    const planCreationOperationStatus = planStore.getCreatePlanFromTemplateOperationStatus(planId);
    return planCreationOperationStatus === OperationStatus.Running;
}
/**
 * Check if the plan can access goals based on user permissions, container permissions, and plan creation info
 * @param plan The plan to check goals access for
 * @param canUserAccessGoals Whether the user has access to goals (from userPermissions.canAccessGoals)
 * @param configProvider The config provider to get flight flags from
 * @param containerGoalsPermissions Whether the container allows using goals (from containerPermissions.canUseGoals)
 * @returns Whether the plan can access goals
 */ export function canPlanAccessGoals(plan, canUserAccessGoals, configProvider, containerGoalsPermissions) {
    // Check if the user has copilot access and if the container supports FAB and goals in plan
    return getIsGoalsEnabled(plan, canUserAccessGoals, configProvider) && containerGoalsPermissions;
}
/**
 * Get whether goals are enabled for a plan
 * @param plan The plan to check goals access for, or null if no plan is available
 * @param canUserAccessGoals Whether the user has access to goals
 * @param configProvider The config provider to get flight flags from
 * @returns Whether goals are enabled for the plan
 */ export function getIsGoalsEnabled(plan, canUserAccessGoals, configProvider) {
    const config = configProvider();
    // If plan was created by Teams Channel Agent, goals is enabled for such plans
    const isPlanCreatedByTeamsChannelAgent = plan && plan.creationInfo.scenarioId === ContextScenarioConstants.teamsChannelAgentContext && config.flights.EnableGoalsInWorkbackPlan;
    // Goals is enabled if user can access goals and either the flight is enabled or the plan was created by Teams Channel Agent
    const isGoalsEnabled = canUserAccessGoals && (config.flights.EnableGoalsInPlan || isPlanCreatedByTeamsChannelAgent);
    return !!isGoalsEnabled;
}
/**
 * Check if goals feature should be shown for a plan (independent of user edit permissions).
 * This enables read-only goals access for M365 users while P1+/Copilot users get full edit access.
 * @param plan The plan to check goals feature visibility for
 * @param configProvider The config provider to get flight flags from
 * @param containerGoalsPermissions Whether the container allows using goals (from containerPermissions.canUseGoals)
 * @returns Whether the goals feature should be shown
 */ export function canPlanShowGoals(plan, configProvider, containerGoalsPermissions) {
    const config = configProvider();
    // Goals are never shown in ToDo container
    if (plan.container.containerType === ContainerType.ToDo) {
        return false;
    }
    // If plan was created by Teams Channel Agent, goals is enabled for such plans
    const isPlanCreatedByTeamsChannelAgent = plan && plan.creationInfo.scenarioId === ContextScenarioConstants.teamsChannelAgentContext && config.flights.EnableGoalsInWorkbackPlan;
    // Goals feature is shown if either the flight is enabled or the plan was created by Teams Channel Agent
    const isGoalsFlightEnabled = config.flights.EnableGoalsInPlan || isPlanCreatedByTeamsChannelAgent;
    return !!(plan && isGoalsFlightEnabled && containerGoalsPermissions);
}
